/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";

/**
 * Props shared by every card icon.
 *
 * Callers supply the BEM class so each host can own sizing, while colour is
 * inherited from the button via `currentColor` — that lets the card's
 * `--focused` modifier recolours the glyph for free. The
 * exported assets hardcode the design's resting `#C4C4C9` / `#F5F5F7` and the
 * focused button's `#0A0A0C`; every one of those is swapped for `currentColor`
 * here, or the glyph would stay grey on the focused (light-filled) button.
 */
export interface ContentCardIconProps {
  /** Class applied to the root `<svg>`. */
  className?: string;
}

/**
 * Shared viewBox for every glyph in this file — the 32×32 design grid the
 * Figma action buttons export on. Inside a button the icon sits at a (32, 25)
 * offset from the artboard origin, so each path below is the asset translated
 * back to the grid origin by that amount.
 */
const VIEW_BOX: string = "0 0 32 32";

/**
 * Stroke weight the outlined glyphs are drawn at, on the 32 grid. Carried as a
 * constant because two icons share it and it has to scale with the box, not
 * with the rendered size.
 */
const STROKE_WIDTH: string = "2.64966";

/** Rounded play triangle — primary "watch this" affordance. */
export const PlayIcon: FunctionComponent<ContentCardIconProps> = (
  props: ContentCardIconProps
): JSX.Element => {
  const { className = "" } = props;
  return (
    <svg
      className={className}
      viewBox={VIEW_BOX}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M24.5735 15.2354C25.7716 16.1067 25.7716 17.8932 24.5735 18.7645L8.4649 30.4799C7.0227 31.5287 4.9998 30.4986 4.9998 28.7154L4.9998 5.2846C4.9998 3.5014 7.0227 2.4713 8.4649 3.5201L24.5735 15.2354Z" />
    </svg>
  );
};

/** Circled "i" — opens the content detail screen. */
export const InfoIcon: FunctionComponent<ContentCardIconProps> = (
  props: ContentCardIconProps
): JSX.Element => {
  const { className = "" } = props;
  return (
    <svg
      className={className}
      viewBox={VIEW_BOX}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M17.6 11.2H14.4V8H17.6M17.6 24H14.4V14.4H17.6M16 0C13.8989 0 11.8183 0.413852 9.87706 1.21793C7.93586 2.022 6.17203 3.20055 4.68629 4.68629C1.68571 7.68687 0 11.7565 0 16C0 20.2435 1.68571 24.3131 4.68629 27.3137C6.17203 28.7994 7.93586 29.978 9.87706 30.7821C11.8183 31.5861 13.8989 32 16 32C20.2435 32 24.3131 30.3143 27.3137 27.3137C30.3143 24.3131 32 20.2435 32 16C32 13.8989 31.5861 11.8183 30.7821 9.87706C29.978 7.93586 28.7994 6.17203 27.3137 4.68629C25.828 3.20055 24.0641 2.022 22.1229 1.21793C20.1817 0.413852 18.1012 0 16 0Z" />
    </svg>
  );
};

/**
 * Outlined trash can — removes a recording.
 *
 * Stroked rather than filled, so it carries `fill="none"` and paints through
 * `stroke`: the design draws it as an outline and a filled render would read as
 * a solid black slab.
 */
export const DeleteIcon: FunctionComponent<ContentCardIconProps> = (
  props: ContentCardIconProps
): JSX.Element => {
  const { className = "" } = props;
  return (
    <svg
      className={className}
      viewBox={VIEW_BOX}
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M6.3446 8.6268V23.5496C6.3446 26.4763 8.7172 28.8489 11.644 28.8489H20.5074C23.4342 28.8489 25.8067 26.4763 25.8067 23.5496V8.6268M12.1225 7.4598V4.3248C12.1225 3.5931 12.7156 3 13.4473 3H18.7041C19.4357 3 20.0289 3.5931 20.0289 4.3248V7.4598M2.9998 8.3222H28.9998M12.5785 13.9478V23.2299M19.5728 13.9478L19.5728 23.2299"
        stroke="currentColor"
        strokeWidth={STROKE_WIDTH}
        strokeLinecap="round"
      />
    </svg>
  );
};

/**
 * Counter-clockwise restore arrow — the design's single "undo" glyph, shared by
 * {@link UndeleteIcon} and drawn as an arc plus a solid arrowhead. The arc is
 * stroked and the head filled, so the two parts carry their paint separately.
 */
export const UnsaveIcon: FunctionComponent<ContentCardIconProps> = (
  props: ContentCardIconProps
): JSX.Element => {
  const { className = "" } = props;
  return (
    <svg
      className={className}
      viewBox={VIEW_BOX}
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M6.72876 19.3966C6.72876 25.2527 11.4761 30.0001 17.3323 30.0001C23.1885 30.0001 27.9359 25.2527 27.9359 19.3966C27.9359 13.5404 23.1885 8.79297 17.3323 8.79297H10.9671"
        stroke="currentColor"
        strokeWidth="2.94849"
        strokeLinecap="round"
      />
      <path
        d="M14.5629 13.5782V3.99067C14.5629 3.15411 13.6197 2.66518 12.9361 3.14734L6.18685 7.90756C5.60629 8.31702 5.60345 9.17696 6.18129 9.59025L12.9306 14.4176C13.6135 14.9061 14.5629 14.4179 14.5629 13.5782Z"
        fill="currentColor"
      />
    </svg>
  );
};

/**
 * Restore arrow — puts a deleted recording back (My Hub Deleted tab). The same
 * glyph the design gives Unsave, so it delegates rather than repeating the
 * paths: the two actions are both "undo the last state change", and a divergence
 * here would be a silent drift from the mock rather than a deliberate choice.
 */
export const UndeleteIcon: FunctionComponent<ContentCardIconProps> = (
  props: ContentCardIconProps
): JSX.Element => <UnsaveIcon {...props} />;

/**
 * Outlined trash can — the irreversible counterpart to {@link DeleteIcon}, used
 * by the Deleted tab's per-row and bulk "permanently delete" affordances. The
 * design gives it the same glyph as Delete and separates the two by label and
 * by the confirm dialog, not by the icon.
 */
export const PermanentlyDeleteIcon: FunctionComponent<ContentCardIconProps> = (
  props: ContentCardIconProps
): JSX.Element => <DeleteIcon {...props} />;

/**
 * Tick in a filled circle — keeps a recording past its expiry (Figma
 * `ic_tick_circle`, InfoCards › Type=Movie, node 1961:24411). The export's
 * backing black circle is dropped: the tick is a cut-out, so the button shows
 * through it, the same as InfoIcon's "i".
 */
export const SaveIcon: FunctionComponent<ContentCardIconProps> = (
  props: ContentCardIconProps
): JSX.Element => {
  const { className = "" } = props;
  return (
    <svg
      className={className}
      viewBox={VIEW_BOX}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M16 1C7.75 1 1 7.75 1 16C1 24.25 7.75 31 16 31C24.25 31 31 24.25 31 16C31 7.75 24.25 1 16 1ZM13 23.5L5.5 16L7.615 13.885L13 19.255L24.385 7.87L26.5 10L13 23.5Z" />
    </svg>
  );
};

/**
 * Arrow leaving to the top-right — extends a recording's expiry.
 *
 * New in this pass: the design does give EXTEND a glyph (`ic_arrow_top_right`),
 * which the popup's catalog previously assumed it did not.
 */
export const ExtendIcon: FunctionComponent<ContentCardIconProps> = (
  props: ContentCardIconProps
): JSX.Element => {
  const { className = "" } = props;
  return (
    <svg
      className={className}
      viewBox={VIEW_BOX}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M2 27.18L23.18 6H10V2H30V22H26V8.82L4.82 30L2 27.18Z" />
    </svg>
  );
};

/**
 * Filled disc with the bar cut out of it — stops a recording that is in
 * progress.
 *
 * One path, not two shapes: the export is a boolean subtract, so the bar is a
 * hole rather than a lighter stripe painted over the disc. That is what makes it
 * read on both states — the button shows through the bar, dark on the focused
 * white fill and translucent on the resting one — where the two-tone version it
 * replaces needed a hardcoded light bar and a red disc to manage the same trick.
 *
 * Wrapped in a translate rather than having its coordinates rewritten: the asset
 * is exported at its own 28×28 bounds, and the 2px offset centres it in this
 * file's 32 grid, where the other glyphs' tight bounds already sit.
 */
export const StopIcon: FunctionComponent<ContentCardIconProps> = (
  props: ContentCardIconProps
): JSX.Element => {
  const { className = "" } = props;
  return (
    <svg
      className={className}
      viewBox={VIEW_BOX}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <g transform="translate(2 2)">
        <path d="M14 0C21.7318 0.000214404 28 6.26815 28 14C28 21.7319 21.7318 27.9998 14 28C6.26801 28 0 21.732 0 14C0 6.26801 6.26801 0 14 0ZM6.80762 12.2305C5.42699 12.2305 4.30776 13.3499 4.30762 14.7305C4.30762 16.1112 5.42691 17.2305 6.80762 17.2305H21.1924C22.5729 17.2302 23.6924 16.111 23.6924 14.7305C23.6922 13.35 22.5728 12.2307 21.1924 12.2305H6.80762Z" />
      </g>
    </svg>
  );
};
